import React, { useEffect, useMemo, useState } from 'react';
import Axios from 'axios';
import dayjs from 'dayjs';
import {
  Modal,
  Form,
  Input,
  Select,
  DatePicker,
  Switch,
  Row,
  Col,
  Typography,
  Divider,
  Button,
  Cascader,
  Table,
  message,
} from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { StatusBadge } from '../../common';
import config from '../../Config/key';
import { userRole } from '../../Config/userRole';
import { surveyCode } from '../../Config/surveyCode';
import { surveyStatus } from '../../Config/surveyStatus';
import {
  DEPT_KEYS,
  PATH_SEP,
  buildOrgCascaderOptions,
  validateDepartmentName,
} from '../../../utils/orgRosterHelper';
import { extractAxiosErrorMessage } from '../../../utils/csvUploadHelper';
import { useCompanyRule, validateCompany } from '../../../utils/companyRule';
import {
  generateInitialPassword,
  validateInitialPassword,
  INITIAL_PASSWORD_RULE_TEXT,
  INITIAL_PASSWORD_ERROR,
} from '../../../utils/initialPassword';

/**
 * UserFormModal — ユーザー情報の登録・編集フォーム（受講者管理画面）
 * ------------------------------------------------------------------
 * 「ユーザー単件登録・編集」PJ Ph.2 で新設。mode で登録／編集を切り替える単一コンポーネント。
 *   - mode='edit'   … 既存ユーザーのプロフィール編集（Ph.2 で実装）
 *   - mode='create' … 新規登録（Ph.3 で実装。POST /api/users・role 9 限定）
 *
 * 設計の要点（開発計画書 §2.1〜§2.6）:
 *   - 他画面が原本を管理する項目（権限・企業グループ・所属指定(E3)・閲覧可能シーズン・
 *     初期パスワード・パスワード変更フラグ）は「表示のみ」とし、変更経路を一本化する。
 *   - 所属は組織表（確定バージョン）がある企業では Cascader でノードからのみ選択し、
 *     保存前に validate-roster で再検証する。無い企業では企業マスターの経路から選択し、
 *     足りなければ「追加」で候補を増やす（マスターへの反映はサーバーが保存時に行う）。
 *   - 役職・等級・雇用区分・出向も同じ「選択 + 追加」パターン（自由入力なし）。
 *   - 保存は「変更された項目のみ」を PATCH する（サーバーは body に存在するキーだけ更新する）。
 *   - 新規登録（create）は「① アカウント（メール・初期パスワード・権限）」を追加表示し、
 *     ⑤ 状態は集計対象外（inactive）のみ表示、⑥ 変更履歴は表示しない（履歴はサーバーが 1 件作成する）。
 *     登録完了後は初期パスワードを画面に表示する（メールは送信しない）。
 *
 * 配色・余白は tokens.css の CSS 変数のみを使用し、新規 HEX・`.ant-*` オーバーライドは追加しない。
 */

const { Text } = Typography;

// 現在値が選択肢（組織表・企業マスター）に無い場合に先頭へ差し込む番兵値。
// この値のまま保存した場合は「所属は変更なし」として送信しない（開発計画書 Q13）。
const CURRENT_VALUE_KEY = '__current__';

// 日付項目の表示・送信フォーマット
const DATE_FORMAT = 'YYYY-MM-DD';

// 所属の最大階層数（department〜department7）
const MAX_DEPT_LEVEL = DEPT_KEYS.length;

// 発行チケットの「実施済み」ステータス（RespondentMgrPage の survey_status_complete と同値）
const SURVEY_STATUS_COMPLETE = 5;

// 性別（DB は 1〜4 の数値で保持）
const SEX_OPTIONS = [
  { value: 1, label: '男性' },
  { value: 2, label: '女性' },
  { value: 3, label: 'その他' },
  { value: 4, label: '回答しない' },
];

// 企業マスター（GSD）から選択する属性項目
const MASTER_FIELDS = [
  { key: 'position', label: '役職' },
  { key: 'level_code', label: '等級' },
  { key: 'emp_type', label: '雇用区分' },
  { key: 'go_out', label: '出向' },
];

// 新規登録で選択できる権限（サーバーの許可値 1・3・7 と一致させる）
const CREATE_ROLE_OPTIONS = [
  { value: 1, label: '受講者' },
  { value: 3, label: '企業管理者' },
  { value: 7, label: 'シーズン管理者' },
];

// 等幅フォント（初期パスワードの表示・入力用。tokens.css に等幅トークンが無いためリテラル）
const MONOSPACE_FONT = 'Menlo, Consolas, monospace';

// 新規登録で送信する文字列項目（サーバーのホワイトリストと一致。空文字は送らない）
const CREATE_TEXT_FIELDS = [
  'lastname',
  'name',
  'lastname_yomi',
  'name_yomi',
  'emp_no',
  'company',
  'position',
  'level_code',
  'emp_type',
  'go_out',
  'work_location',
  'work_time_rule',
  'work_time',
  'join_type',
  'job_type',
];

// 自由入力の文字列項目（差分比較の対象）
const TEXT_FIELDS = [
  'email',
  'lastname',
  'name',
  'lastname_yomi',
  'name_yomi',
  'emp_no',
  'company',
  'position',
  'level_code',
  'emp_type',
  'go_out',
  'work_location',
  'work_time_rule',
  'work_time',
  'join_type',
  'job_type',
];

// 補足説明（ヒント）の共通スタイル
const HINT_STYLE = {
  fontSize: 'var(--text-xs)',
  color: 'var(--grey-65)',
  lineHeight: 'var(--leading-normal)',
};

// 変更履歴の注意書き（黄色の案内ボックス）
const NOTICE_STYLE = {
  marginBottom: 'var(--space-3)',
  padding: 'var(--space-2) var(--space-3)',
  border: '1px solid var(--yellow-hover)',
  background: 'var(--yellow-background)',
  borderRadius: 'var(--radius-md)',
  fontSize: 'var(--text-xs)',
  lineHeight: 'var(--leading-normal)',
};

// 「追加」小モーダルの本文レイアウト
const ADD_FORM_STYLE = {
  display: 'flex',
  flexDirection: 'column',
  gap: 'var(--space-2)',
};

// 「選択 + 追加」項目のレイアウト（mockup の gap 8px = --space-2）
const SELECT_ROW_STYLE = {
  display: 'flex',
  gap: 'var(--space-2)',
  alignItems: 'center',
};

const trimText = (value) =>
  value === undefined || value === null ? '' : String(value).trim();

// 所属（department〜department7）の非空階層を配列で返す
const deptPathArray = (src) =>
  DEPT_KEYS.map((key) => trimText(src ? src[key] : '')).filter((v) => v !== '');

// 所属を '／' 連結のフルパス文字列にする
const joinDeptPath = (src) => deptPathArray(src).join(PATH_SEP);

// Date 文字列 → dayjs（不正値は null）
const toDayjs = (value) => {
  if (!value) return null;
  const d = dayjs(value);
  return d.isValid() ? d : null;
};

// 変更履歴の日時表示（解釈できない値は原文のまま表示する）
const formatHistoryDate = (value) => {
  if (!value) return '—';
  const d = dayjs(value);
  return d.isValid() ? d.format('YYYY/MM/DD HH:mm') : String(value);
};

// 値が空なら「—」を表示する
const dashIfEmpty = (value) => (trimText(value) === '' ? '—' : String(value));

// 変更履歴の並べ替え用（新しい順）。解釈できない日時は 0 として最後尾に送る
const historyTime = (value) => {
  const time = new Date(value).getTime();
  return isNaN(time) ? 0 : time;
};

// Cascader の options に指定の経路（名称配列）が存在するか
const pathExistsInOptions = (options, pathArray) => {
  if (!pathArray || pathArray.length === 0) return true;
  let level = options || [];
  for (let i = 0; i < pathArray.length; i += 1) {
    const found = level.find((option) => option.value === pathArray[i]);
    if (!found) return false;
    level = found.children || [];
  }
  return true;
};

/**
 * 「選択 + 追加」入力（役職・等級・雇用区分・出向・所属（組織表なし））
 * ------------------------------------------------------------------
 * 企業マスターの候補から選ぶ Select と、候補を増やす「追加」ボタンの組。
 * 追加した値はローカルの選択肢に加えて選択状態にするだけで、企業マスターへの
 * 登録はユーザー情報の保存時にサーバーが行う（キャンセルすれば登録されない）。
 * Form.Item の子として value / onChange を受け取る。
 */
function SelectWithAdd({
  value,
  onChange,
  options = [],
  itemLabel,
  addButtonLabel,
  addTitle,
  addFieldLabel,
  addPlaceholder,
  addHint,
  validateValue,
  normalizeValue,
  onAddOption,
}) {
  const [addOpen, setAddOpen] = useState(false);
  const [addValue, setAddValue] = useState('');
  const [addError, setAddError] = useState('');

  const openAdd = () => {
    setAddValue('');
    setAddError('');
    setAddOpen(true);
  };

  const confirmAdd = () => {
    const raw = trimText(addValue);
    const next = normalizeValue ? normalizeValue(raw) : raw;
    const error = validateValue
      ? validateValue(next)
      : next === ''
      ? `${itemLabel}を入力してください`
      : '';
    if (error) {
      setAddError(error);
      return;
    }
    if (onAddOption) onAddOption(next);
    if (onChange) onChange(next);
    setAddOpen(false);
  };

  return (
    <>
      <div style={SELECT_ROW_STYLE}>
        <Select
          showSearch
          allowClear
          style={{ flex: 1, minWidth: 0 }}
          placeholder="選択してください"
          optionFilterProp="label"
          options={options}
          value={value === undefined || value === '' ? undefined : value}
          onChange={(next) => onChange && onChange(next === undefined ? '' : next)}
        />
        <Button icon={<PlusOutlined />} onClick={openAdd}>
          {addButtonLabel || '追加'}
        </Button>
      </div>
      <Modal
        open={addOpen}
        title={addTitle}
        okText="追　加"
        cancelText="キャンセル"
        maskClosable={false}
        destroyOnHidden
        onOk={confirmAdd}
        onCancel={() => setAddOpen(false)}
      >
        {/* 親フォームの内側に <Form> を入れ子にしないよう、素のラベル + Input で構成する */}
        <div style={ADD_FORM_STYLE}>
          <div>
            <Text type="danger">*</Text>{' '}
            <Text>{addFieldLabel || itemLabel}</Text>
          </div>
          <Input
            value={addValue}
            status={addError ? 'error' : ''}
            placeholder={addPlaceholder}
            onChange={(e) => {
              setAddValue(e.target.value);
              if (addError) setAddError('');
            }}
            onPressEnter={confirmAdd}
          />
          {addError ? (
            <Text type="danger" style={{ fontSize: 'var(--text-xs)' }}>
              {addError}
            </Text>
          ) : null}
          <div style={HINT_STYLE}>{addHint}</div>
        </div>
      </Modal>
    </>
  );
}

function UserFormModal({
  open,
  mode = 'edit',
  user,
  groupInfo,
  orgTree,
  surveys = [],
  surveySeasons = [],
  onSaved,
  onCancel,
}) {
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);
  // 「追加」で増やしたローカル選択肢（保存時にサーバーが企業マスターへ反映する）
  const [addedOptions, setAddedOptions] = useState({});
  // 発行チケットの「本人ダウンロード回数」（{ [survey_token_id]: 回数 }）。未取得は 0 として表示する
  const [downloadCounts, setDownloadCounts] = useState({});

  const isEdit = mode !== 'create';
  const groupCode = (user && user.group_code) || (groupInfo && groupInfo.group_code) || '';
  // 所属指定（E3）の表示用。旧形式（10 枠を '' で埋めた配列）の空欄は表示しない
  const e3Departments = ((user && user.e3_department) || []).filter(
    (dep) => typeof dep === 'string' && dep.trim() !== ''
  );

  // 系列会社ありグループでは会社名を系列会社一覧から選択する
  // （GET /api/group/:code/company-list の names。取得完了まで・失敗時はグループの系列会社マスタ）
  const companyRule = useCompanyRule(groupCode, groupInfo);
  // 会社名の選択肢（マスタの active な名称。編集時に現在値がマスタに無ければ先頭に表示して保持できるようにする）
  const companyOptions = useMemo(() => {
    if (!companyRule.enabled) return [];
    const list = companyRule.names.map((name) => ({ value: name, label: name }));
    const current = isEdit ? trimText(user ? user.company : '') : '';
    if (current !== '' && !companyRule.names.includes(current)) {
      list.unshift({ value: current, label: `（マスタ未登録）${current}` });
    }
    return list;
  }, [companyRule, isEdit, user]);

  // 組織表（確定バージョン）の有無で所属の入力方式が変わる
  const hasOrgTree = !!(orgTree && orgTree.version);
  // 系列会社ありグループ・組織表ありでは、所属 Cascader を選択中の会社名（系列会社）の子孫に絞る
  const companyFilterActive = companyRule.enabled && hasOrgTree;
  // 選択中の会社名。null = モーダルを開いてから未操作（初期値＝保存済みの会社名を使う）
  const [pickedCompany, setPickedCompany] = useState(null);
  const initialCompany = isEdit ? trimText(user ? user.company : '') : '';
  const cascaderCompany = companyFilterActive
    ? pickedCompany !== null
      ? pickedCompany
      : initialCompany
    : null;
  // 会社名未選択なら所属 Cascader を無効化する（先に会社名を選ばせる）
  const cascaderNeedsCompany = companyFilterActive && cascaderCompany === '';
  const cascaderBaseOptions = useMemo(
    () =>
      hasOrgTree
        ? buildOrgCascaderOptions(
            orgTree.units,
            cascaderCompany !== null ? { company: cascaderCompany } : {}
          )
        : [],
    [hasOrgTree, orgTree, cascaderCompany]
  );

  // 現在の所属（保存済みの値）
  const currentDeptArray = useMemo(() => deptPathArray(user), [user]);
  const currentDeptPath = currentDeptArray.join(PATH_SEP);

  // 企業マスターの所属候補（組織表が無い企業で使用）
  const masterDeptPaths = useMemo(
    () => ((groupInfo && groupInfo.department) || []).map((v) => trimText(v)).filter((v) => v !== ''),
    [groupInfo]
  );

  // 現在値が選択肢に無い場合は「(現在の値) …」として先頭に出す（開発計画書 Q13）
  const needsCurrentDeptOption = useMemo(() => {
    if (currentDeptArray.length === 0) return false;
    return hasOrgTree
      ? !pathExistsInOptions(cascaderBaseOptions, currentDeptArray)
      : !masterDeptPaths
          .concat(addedOptions.department || [])
          .includes(currentDeptPath);
  }, [
    currentDeptArray,
    currentDeptPath,
    hasOrgTree,
    cascaderBaseOptions,
    masterDeptPaths,
    addedOptions.department,
  ]);

  const cascaderOptions = useMemo(() => {
    if (!needsCurrentDeptOption) return cascaderBaseOptions;
    return [
      { value: CURRENT_VALUE_KEY, label: `(現在の値) ${currentDeptPath}` },
    ].concat(cascaderBaseOptions);
  }, [needsCurrentDeptOption, cascaderBaseOptions, currentDeptPath]);

  const deptSelectOptions = useMemo(() => {
    const list = masterDeptPaths
      .concat(addedOptions.department || [])
      .filter((v, i, arr) => arr.indexOf(v) === i)
      .map((path) => ({ value: path, label: path }));
    if (needsCurrentDeptOption) {
      list.unshift({
        value: CURRENT_VALUE_KEY,
        label: `(現在の値) ${currentDeptPath}`,
      });
    }
    return list;
  }, [masterDeptPaths, addedOptions.department, needsCurrentDeptOption, currentDeptPath]);

  // 役職・等級・雇用区分・出向の選択肢（企業マスター + 追加分 + 現在値）
  const masterOptions = useMemo(() => {
    const result = {};
    MASTER_FIELDS.forEach(({ key }) => {
      const base = ((groupInfo && groupInfo[key]) || [])
        .map((v) => trimText(v))
        .filter((v) => v !== '');
      const merged = base
        .concat(addedOptions[key] || [])
        .filter((v, i, arr) => arr.indexOf(v) === i);
      const list = merged.map((v) => ({ value: v, label: v }));
      const current = trimText(user ? user[key] : '');
      if (current !== '' && !merged.includes(current)) {
        // 現在値がマスターから消えている場合も編集を妨げない（開発計画書 Q13）
        list.unshift({ value: current, label: `(現在の値) ${current}` });
      }
      result[key] = list;
    });
    return result;
  }, [groupInfo, addedOptions, user]);

  // フォーム初期値（編集 = 最新のユーザー文書 / 新規登録 = 会社名と権限の既定値のみ）
  const initialValues = useMemo(() => {
    if (!isEdit) {
      // 会社名の初期値は企業グループ名（編集可）。権限の既定は受講者
      // ※ 系列会社ありグループは系列会社マスタから選ばせるため初期値は空
      return {
        company: companyRule.enabled
          ? undefined
          : trimText(groupInfo && groupInfo.group_name),
        role: 1,
      };
    }
    if (!user) return {};
    const values = {
      email: trimText(user.email),
      lastname: trimText(user.lastname),
      name: trimText(user.name),
      lastname_yomi: trimText(user.lastname_yomi),
      name_yomi: trimText(user.name_yomi),
      emp_no: trimText(user.emp_no),
      // 系列会社ありグループは Select のため、空は undefined にしてプレースホルダーを表示する
      company:
        companyRule.enabled && trimText(user.company) === ''
          ? undefined
          : trimText(user.company),
      sex:
        user.sex === undefined || user.sex === null || user.sex === ''
          ? undefined
          : Number(user.sex),
      birthday: toDayjs(user.birthday),
      join_company: toDayjs(user.join_company),
      work_location: trimText(user.work_location),
      work_time_rule: trimText(user.work_time_rule),
      work_time: trimText(user.work_time),
      join_type: trimText(user.join_type),
      job_type: trimText(user.job_type),
      inactive: !!user.inactive,
    };
    MASTER_FIELDS.forEach(({ key }) => {
      values[key] = trimText(user[key]);
    });
    if (hasOrgTree) {
      values.deptPath = needsCurrentDeptOption
        ? [CURRENT_VALUE_KEY]
        : currentDeptArray.length > 0
        ? currentDeptArray
        : undefined;
    } else {
      values.deptText = needsCurrentDeptOption
        ? CURRENT_VALUE_KEY
        : currentDeptPath;
    }
    return values;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, isEdit, groupInfo, hasOrgTree, needsCurrentDeptOption, currentDeptPath]);

  // モーダルを開くたびに最新のユーザー文書でフォームを初期化する
  const userId = user ? user._id : null;
  useEffect(() => {
    if (!open) return;
    setAddedOptions({});
    setPickedCompany(null);
    form.resetFields();
    form.setFieldsValue(initialValues);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, userId]);

  // ⑦ 発行チケットの「本人ダウンロード回数」を取得する（編集モードのみ）
  // 表示の補助情報のため待ち合わせはせず、失敗しても画面は 0 件表示のまま継続する
  useEffect(() => {
    if (!open || !isEdit) return;
    setDownloadCounts({});
    const tokenIds =
      user && Array.isArray(user.survey_token)
        ? user.survey_token.map((token) => token._id).filter((id) => !!id)
        : [];
    if (tokenIds.length === 0) return;
    let canceled = false;
    Axios.post('/api/surveyToken/download-counts', { tokenIds })
      .then((res) => {
        if (canceled) return;
        if (res.data && res.data.success) setDownloadCounts(res.data.counts || {});
      })
      .catch((error) => {
        console.log({ error });
      });
    return () => {
      canceled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, userId, isEdit]);

  // 「追加」で増やした候補をローカル選択肢へ反映する
  const addOption = (key, value) => {
    setAddedOptions((prev) => ({
      ...prev,
      [key]: (prev[key] || []).concat(value),
    }));
  };

  // 所属フルパスの入力検証（'／' 区切り・空階層なし・最大 7 階層・重複なし）
  const validateDeptPath = (value) => {
    if (value === '') return '所属（フルパス）を入力してください';
    const parts = value.split(PATH_SEP);
    if (parts.some((part) => trimText(part) === '')) {
      return '途中の階層を空にすることはできません';
    }
    if (parts.length > MAX_DEPT_LEVEL) {
      return `階層は最大 ${MAX_DEPT_LEVEL} 階層までです`;
    }
    // 各階層の所属名の入力規則（制御文字禁止・50 文字以内。'／' は分割済みのため含まれない）
    for (let i = 0; i < parts.length; i++) {
      const nameError = validateDepartmentName(parts[i]);
      if (nameError) return `所属${i + 1}: ${nameError}`;
    }
    if (masterDeptPaths.concat(addedOptions.department || []).includes(value)) {
      return '既に候補にある所属です';
    }
    return '';
  };

  // 所属フルパスの正規化（各階層の前後空白を除去して '／' で連結し直す）
  const normalizeDeptPath = (value) =>
    value
      .split(PATH_SEP)
      .map((part) => trimText(part))
      .join(PATH_SEP);

  // 属性項目（役職等）の入力検証
  const validateMasterValue = (key, label) => (value) => {
    if (value === '') return `${label}を入力してください`;
    const base = ((groupInfo && groupInfo[key]) || []).map((v) => trimText(v));
    if (base.concat(addedOptions[key] || []).includes(value)) {
      return `既に候補にある${label}です`;
    }
    return '';
  };

  /**
   * 所属の差分を解決する。
   * @returns {Object|null} 送信する department〜department7（変更が無い・番兵のままなら null）
   */
  const resolveDeptFields = (values) => {
    let nextArray;
    if (hasOrgTree) {
      const selected = values.deptPath;
      if (
        Array.isArray(selected) &&
        selected.length === 1 &&
        selected[0] === CURRENT_VALUE_KEY
      ) {
        return null; // 「(現在の値)」のまま = 変更なし
      }
      nextArray = (selected || []).map((v) => trimText(v)).filter((v) => v !== '');
    } else {
      const selected = trimText(values.deptText);
      if (selected === CURRENT_VALUE_KEY) return null;
      nextArray =
        selected === ''
          ? []
          : selected
              .split(PATH_SEP)
              .map((v) => trimText(v))
              .filter((v) => v !== '');
    }
    if (nextArray.length > MAX_DEPT_LEVEL) {
      nextArray = nextArray.slice(0, MAX_DEPT_LEVEL);
    }
    const before = DEPT_KEYS.map((key) => trimText(user[key]));
    const after = DEPT_KEYS.map((key, index) =>
      nextArray[index] !== undefined ? nextArray[index] : ''
    );
    if (after.every((v, index) => v === before[index])) return null;
    const payload = {};
    DEPT_KEYS.forEach((key, index) => {
      payload[key] = after[index];
    });
    return payload;
  };

  // 変更された項目だけを payload に積む（サーバーは body にあるキーだけ更新する）
  const buildPayload = (values) => {
    const payload = {};
    TEXT_FIELDS.forEach((key) => {
      const after = trimText(values[key]);
      if (after !== trimText(user[key])) payload[key] = after;
    });
    // 性別（未選択は '' を送って未設定にする）
    const afterSex =
      values.sex === undefined || values.sex === null || values.sex === ''
        ? ''
        : Number(values.sex);
    const beforeSex =
      user.sex === undefined || user.sex === null || user.sex === ''
        ? ''
        : Number(user.sex);
    if (String(afterSex) !== String(beforeSex)) payload.sex = afterSex;
    // 日付（未選択は '' を送って未設定にする）
    ['birthday', 'join_company'].forEach((key) => {
      const afterDate = values[key] ? dayjs(values[key]).format(DATE_FORMAT) : '';
      const beforeDayjs = toDayjs(user[key]);
      const beforeDate = beforeDayjs ? beforeDayjs.format(DATE_FORMAT) : '';
      if (afterDate !== beforeDate) payload[key] = afterDate;
    });
    // 集計対象外
    if (!!values.inactive !== !!user.inactive) payload.inactive = !!values.inactive;
    // 所属
    const deptFields = resolveDeptFields(values);
    if (deptFields) Object.assign(payload, deptFields);
    return payload;
  };

  /**
   * 新規登録の所属を department〜department7 へ展開する
   * 空の階層は送信しない（サーバーは body に無いキーを未設定として扱う）
   * @returns {Object} department 系フィールド（未選択なら空オブジェクト）
   */
  const resolveCreateDeptFields = (values) => {
    let nextArray;
    if (hasOrgTree) {
      nextArray = (values.deptPath || [])
        .map((v) => trimText(v))
        .filter((v) => v !== '');
    } else {
      const selected = trimText(values.deptText);
      nextArray =
        selected === ''
          ? []
          : selected
              .split(PATH_SEP)
              .map((v) => trimText(v))
              .filter((v) => v !== '');
    }
    if (nextArray.length > MAX_DEPT_LEVEL) {
      nextArray = nextArray.slice(0, MAX_DEPT_LEVEL);
    }
    const payload = {};
    nextArray.forEach((value, index) => {
      payload[DEPT_KEYS[index]] = value;
    });
    return payload;
  };

  // 新規登録の送信データを組み立てる（空の項目はキーごと送らない）
  const buildCreatePayload = (values) => {
    const payload = {
      email: trimText(values.email),
      // 前後の空白はコピー＆ペースト由来のことが多いため除去して送信する
      // （途中の空白は検証エラーにするため、ここでは詰めない）
      password: trimText(values.password),
      group_code: (groupInfo && groupInfo.group_code) || '',
      role: values.role === undefined || values.role === null ? 1 : Number(values.role),
    };
    CREATE_TEXT_FIELDS.forEach((key) => {
      const value = trimText(values[key]);
      if (value !== '') payload[key] = value;
    });
    if (values.sex !== undefined && values.sex !== null && values.sex !== '') {
      payload.sex = Number(values.sex);
    }
    ['birthday', 'join_company'].forEach((key) => {
      if (values[key]) payload[key] = dayjs(values[key]).format(DATE_FORMAT);
    });
    // 集計対象外（サーバー既定は false のため ON の時だけ送る）
    if (values.inactive) payload.inactive = true;
    Object.assign(payload, resolveCreateDeptFields(values));
    return payload;
  };

  // 登録完了の案内（初期パスワードはこの画面でのみ通知する）
  const showCreatedInfo = (createdUser, password) => {
    Modal.success({
      title: 'ユーザーを登録しました',
      okText: '閉じる',
      content: (
        <div>
          <div style={{ marginBottom: 'var(--space-2)' }}>
            <Text>ログインID：</Text>
            <Text strong>{trimText(createdUser && createdUser.email)}</Text>
          </div>
          <div style={{ marginBottom: 'var(--space-3)' }}>
            <Text>初期パスワード：</Text>
            <Text strong code style={{ fontFamily: MONOSPACE_FONT }}>
              {password}
            </Text>
          </div>
          <div style={HINT_STYLE}>
            メールは送信していません。初期パスワードを本人へ連絡してください。
          </div>
        </div>
      ),
    });
  };

  // 新規登録の保存処理
  const handleCreate = async (values) => {
    const payload = buildCreatePayload(values);
    setSaving(true);
    try {
      const rosterOk = await validateRoster(payload);
      if (!rosterOk) return;
      const res = await Axios.post(config.USER_SERVER, payload);
      if (res.data && res.data.success) {
        message.success('登録しました');
        if (onSaved) onSaved(res.data.user, { created: true });
        showCreatedInfo(res.data.user, payload.password);
      } else {
        message.error((res.data && res.data.error) || '登録に失敗しました');
      }
    } catch (error) {
      console.log({ error });
      message.error(extractAxiosErrorMessage(error, '登録に失敗しました'));
    } finally {
      setSaving(false);
    }
  };

  // ログインID（メールアドレス）変更時の確認ダイアログ
  const confirmEmailChange = () =>
    new Promise((resolve) => {
      Modal.confirm({
        title: 'ログインIDが変わります',
        content:
          '本人へ新しいメールアドレスを連絡してください。続行しますか？',
        okText: 'はい',
        cancelText: 'いいえ',
        onOk: () => resolve(true),
        onCancel: () => resolve(false),
      });
    });

  // 組織表がある企業では保存前に所属を再検証する（不一致は保存を中止）
  // 系列会社ありグループは会社名（系列会社）も送り、会社名の変更も照合対象にする
  // （会社名だけで所属が空の行も照合する＝サーバーは会社直下として扱う）
  const validateRoster = async (payload) => {
    if (!hasOrgTree) return true;
    const affiliate = companyRule.enabled;
    const deptChanged = DEPT_KEYS.some((key) => payload[key] !== undefined);
    const companyChanged = affiliate && payload.company !== undefined;
    if (!deptChanged && !companyChanged) return true;
    // 照合する所属: 変更があれば送信値、会社名だけの変更なら保存済みの所属
    const deptSource = deptChanged ? payload : user || {};
    if (!affiliate && trimText(deptSource.department) === '') return true; // 所属を空にする場合は照合しない
    const row = { row: 1 };
    if (affiliate) {
      row.company = trimText(
        payload.company !== undefined ? payload.company : user ? user.company : ''
      );
    }
    DEPT_KEYS.forEach((key, index) => {
      row[`d${index + 1}`] = deptSource[key] || '';
    });
    const res = await Axios.post(`/api/org/validate-roster/${groupCode}`, {
      rows: [row],
    });
    const data = res.data || {};
    if (data.has_org_master === false) return true; // 組織表が無ければ照合しない
    const result = (data.results || [])[0];
    if (result && result.ok === false) {
      message.error(
        result.field === 'company'
          ? `会社名が組織表と一致しません：${result.error || ''}`
          : `所属が組織表と一致しません：${result.error || ''}`
      );
      return false;
    }
    return true;
  };

  const handleSave = async () => {
    let values;
    try {
      values = await form.validateFields();
    } catch (error) {
      return; // 入力エラーは Form が表示する
    }
    if (!isEdit) {
      await handleCreate(values);
      return;
    }
    const payload = buildPayload(values);
    if (Object.keys(payload).length === 0) {
      message.info('変更はありません');
      if (onCancel) onCancel();
      return;
    }
    if (payload.email !== undefined) {
      const proceed = await confirmEmailChange();
      if (!proceed) return;
    }
    setSaving(true);
    try {
      const rosterOk = await validateRoster(payload);
      if (!rosterOk) return;
      const res = await Axios.patch(
        `${config.USER_SERVER}/${user._id}/profile`,
        payload
      );
      if (res.data && res.data.success) {
        message.success('保存しました');
        if (onSaved) onSaved(res.data.user);
      } else {
        message.error((res.data && res.data.error) || '保存に失敗しました');
      }
    } catch (error) {
      console.log({ error });
      message.error(extractAxiosErrorMessage(error, '保存に失敗しました'));
    } finally {
      setSaving(false);
    }
  };

  // 変更履歴（新しい順）
  const historyRows = useMemo(() => {
    const list =
      user && Array.isArray(user.change_history) ? user.change_history.slice() : [];
    // antd Table の rowKey に index を使うと非推奨警告が出るため、並べ替え後に安定キーを付与する
    return list
      .sort(
        (a, b) =>
          historyTime(b && b.input_date) - historyTime(a && a.input_date)
      )
      .map((entry, index) => ({ ...entry, _rowKey: `${index}-${entry && entry.input_date}` }));
  }, [user]);

  // 変更履歴テーブルのセル密度（ヘッダー・本文とも小さめの文字・狭い上下パディングで行高を抑える）
  const compactHistoryCell = () => ({
    style: {
      padding: 'var(--space-1) var(--space-2)',
      fontSize: 'var(--text-xs)',
      lineHeight: 'var(--leading-tight)',
      whiteSpace: 'nowrap',
    },
  });
  const historyColumns = [
    {
      title: '変更日時',
      dataIndex: 'input_date',
      key: 'input_date',
      width: 120,
      render: (value) => formatHistoryDate(value),
    },
    // 系列会社ありグループのみ「会社名」列を表示（履歴要素に company を記録しているため）
    ...(companyRule.enabled
      ? [
          {
            title: '会社名',
            dataIndex: 'company',
            key: 'company',
            width: 120,
            render: (value) => dashIfEmpty(value),
          },
        ]
      : []),
    {
      title: '所属',
      key: 'department', // 幅指定なし＝残り幅を使う（列幅合計がテーブル幅を超えて横スクロールが出ないように）,
      render: (text, record) => joinDeptPath(record) || '—',
    },
    {
      title: '役職',
      dataIndex: 'position',
      key: 'position',
      width: 72,
      render: (value) => dashIfEmpty(value),
    },
    {
      title: '等級',
      dataIndex: 'level_code',
      key: 'level_code',
      width: 64,
      render: (value) => dashIfEmpty(value),
    },
    {
      title: '雇用区分',
      dataIndex: 'emp_type',
      key: 'emp_type',
      width: 84,
      render: (value) => dashIfEmpty(value),
    },
    {
      title: '出向',
      dataIndex: 'go_out',
      key: 'go_out',
      width: 64,
      render: (value) => dashIfEmpty(value),
    },
    {
      title: '集計対象外',
      dataIndex: 'inactive',
      key: 'inactive',
      width: 84,
      render: (value) => (value === true ? '対象外' : '—'),
    },
  ].map((column) => ({
    ...column,
    onHeaderCell: compactHistoryCell,
    onCell: compactHistoryCell,
  }));

  // ── ⑦ 発行チケット（編集のみ・読み取り専用） ───────────────────────────
  // 一覧の「発行チケット確認」モーダルと同じ情報を、削除・差戻し・結果確認の操作なしで表示する。
  // サーベイ名の組み立て規則は RespondentMgrPage の viewTicketHandler と同一。
  // 参照先（シーズン・設問セット）が見つからない場合でも例外を出さず、分かる部分だけ表示する。
  const ticketRows = useMemo(() => {
    const list =
      user && Array.isArray(user.survey_token) ? user.survey_token.slice() : [];
    return list
      .map((token, index) => {
        // シーズンIDがあればシーズン名、無ければサーベイコードの名称
        let surveyName = '';
        if (token.survey_season_id) {
          const seasonInfo = (surveySeasons || []).find(
            (item) => item._id === token.survey_season_id
          );
          surveyName = trimText(seasonInfo?.season_name);
        } else {
          surveyName = trimText(surveyCode[token.survey_code]);
        }
        // 設問セットIDがあれば設問セット名を ' : ' で連結（20210 は対象外）
        if (token.question_set_id && token.survey_code !== '20210') {
          const surveyInfo = (surveys || []).find(
            (item) => item._id === token.survey_id
          );
          const setName = trimText(
            surveyInfo?.question_set?.find(
              (item) => item._id === token.question_set_id
            )?.set_name
          );
          if (setName !== '') {
            surveyName = surveyName === '' ? setName : `${surveyName} : ${setName}`;
          }
        }
        // 実施状況（実施済みは提出日時を併記。差戻し履歴があれば付記する）
        let statusText =
          token.survey_status === SURVEY_STATUS_COMPLETE
            ? `実施済み（${formatHistoryDate(token.updatedAt)}）`
            : trimText(surveyStatus[token.survey_status]);
        if (token.was_reverted) statusText = `${statusText}（差戻し）`;
        const madeDate = token.token_make_date || token.createdAt;
        return {
          _rowKey: token._id ? String(token._id) : `ticket-${index}`,
          surveyName: surveyName === '' ? '—' : surveyName,
          madeDate,
          tokenExpire: token.token_expire,
          statusText: statusText === '' ? '—' : statusText,
          dlCount: downloadCounts[String(token._id)] || 0,
        };
      })
      .sort((a, b) => historyTime(b.madeDate) - historyTime(a.madeDate));
  }, [user, surveys, surveySeasons, downloadCounts]);

  const ticketColumns = [
    {
      title: 'サーベイ名',
      dataIndex: 'surveyName',
      key: 'surveyName', // 幅指定なし＝残り幅を使う
    },
    {
      title: '発行日',
      dataIndex: 'madeDate',
      key: 'madeDate',
      width: 120,
      render: (value) => formatHistoryDate(value),
    },
    {
      title: '有効期限',
      dataIndex: 'tokenExpire',
      key: 'tokenExpire',
      width: 120,
      render: (value) => formatHistoryDate(value),
    },
    {
      title: '実施状況',
      dataIndex: 'statusText',
      key: 'statusText',
      width: 190,
    },
    {
      title: 'DL',
      dataIndex: 'dlCount',
      key: 'dlCount',
      width: 48,
    },
  ].map((column) => ({
    ...column,
    onHeaderCell: compactHistoryCell,
    onCell: compactHistoryCell,
  }));

  // 初期パスワードの自動生成（強い規則を必ず満たす 12 文字）
  const handleGeneratePassword = () => {
    form.setFieldsValue({ password: generateInitialPassword() });
    // 既に出ている書式エラーを消すため、その場で再検証する
    form.validateFields(['password']).catch(() => {});
  };

  // 初期パスワードの入力欄からフォーカスが外れたら前後の空白を除去する
  // （コピー＆ペーストで混入しやすいため。途中の空白は詰めず検証エラーで知らせる）
  const handlePasswordBlur = () => {
    const current = form.getFieldValue('password');
    if (typeof current !== 'string') return;
    const trimmed = current.trim();
    if (trimmed === current) return;
    form.setFieldsValue({ password: trimmed });
    form.validateFields(['password']).catch(() => {});
  };

  // セクション見出し（① アカウント 等）
  const sectionTitle = (title) => (
    <>
      <Text strong>{title}</Text>
      <Divider style={{ margin: 'var(--space-2) 0 var(--space-3)' }} />
    </>
  );

  const hint = (textNode) => <span style={HINT_STYLE}>{textNode}</span>;

  const groupLabel = groupInfo && groupInfo.group_name
    ? `${groupInfo.group_name}（${groupInfo.group_code}）`
    : groupCode || '—';

  const modalTitle = isEdit ? (
    <>
      ユーザー情報編集{' '}
      <Text type="secondary" style={{ fontWeight: 'var(--font-regular)' }}>
        {user
          ? `${trimText(user.lastname)} ${trimText(user.name)}（${trimText(
              user.email
            )}）`
          : ''}
      </Text>
    </>
  ) : (
    <>
      ユーザー新規登録{' '}
      <Text type="secondary" style={{ fontWeight: 'var(--font-regular)' }}>
        {groupLabel}
      </Text>
    </>
  );

  return (
    <Modal
      open={open}
      width={800}
      title={modalTitle}
      okText={isEdit ? '保　存' : '登　録'}
      cancelText="キャンセル"
      maskClosable={false}
      forceRender
      confirmLoading={saving}
      onOk={handleSave}
      onCancel={onCancel}
      // content の padding を 0 にして body 自身をスクロール領域にする（スクロールバーがモーダル右端に付く）。
      // Row gutter の負マージンで横スクロールが出ないよう overflowX は hidden にする。
      styles={{
        content: { padding: 0 },
        header: {
          margin: 0,
          padding: 'var(--space-4) var(--space-6)',
          borderBottom: '1px solid var(--grey-20)',
        },
        body: {
          maxHeight: '70vh',
          overflowY: 'auto',
          overflowX: 'hidden',
          padding: 'var(--space-3) var(--space-6) var(--space-5)',
        },
        footer: {
          margin: 0,
          padding: 'var(--space-3) var(--space-6)',
          borderTop: '1px solid var(--grey-20)',
        },
      }}
    >
      {/* Form は forceRender で常時マウントし、useForm インスタンスが未接続にならないようにする
          （開くたびに useEffect で resetFields + setFieldsValue して初期化する） */}
      <Form form={form} layout="vertical" initialValues={initialValues}>
        {user || !isEdit ? (
        <>
          {/* ① アカウント */}
          <div>
            {sectionTitle('① アカウント')}
            <Row gutter={24}>
              <Col span={12}>
                <Form.Item
                  label="メールアドレス（ログインID）"
                  name="email"
                  rules={[
                    { required: true, message: 'メールアドレスを入力してください' },
                    { type: 'email', message: 'メールアドレスの形式が不正です' },
                  ]}
                  extra={hint(
                    isEdit
                      ? '変更時は重複チェックを行います。変更後は本人への通知が必要です'
                      : '登録時に重複チェックを行います。本人がログインする際の ID になります'
                  )}
                >
                  <Input placeholder={isEdit ? undefined : 'user@example.com'} />
                </Form.Item>
              </Col>
              {isEdit ? (
                <Col span={12}>
                  <Form.Item
                    label="パスワード"
                    extra={hint('初期化は一覧の「パスワード」列から行います')}
                  >
                    <Text type="secondary">（この画面では変更しません）</Text>
                  </Form.Item>
                </Col>
              ) : (
                <Col span={12}>
                  {/* 入力欄と「自動生成」を横並びにするため、内側は noStyle の Form.Item に分ける
                      （検証エラーは親の Form.Item がまとめて表示する） */}
                  <Form.Item
                    label="初期パスワード"
                    required
                    extra={hint(
                      `${INITIAL_PASSWORD_RULE_TEXT}。登録後この画面に表示します（メールは送信しません）`
                    )}
                  >
                    <div style={SELECT_ROW_STYLE}>
                      <Form.Item
                        name="password"
                        noStyle
                        rules={[
                          {
                            required: true,
                            // 空白のみの入力は「未入力」として扱う
                            whitespace: true,
                            message: '初期パスワードを入力してください',
                          },
                          {
                            // 前後の空白は除去してから検証する（途中の空白はエラーのまま）
                            // ※ 空白のみの入力は上の required（whitespace）が知らせるため、ここでは検証しない
                            validator: (rule, value) =>
                              !trimText(value) || validateInitialPassword(trimText(value))
                                ? Promise.resolve()
                                : Promise.reject(new Error(INITIAL_PASSWORD_ERROR)),
                          },
                        ]}
                      >
                        <Input
                          style={{ flex: 1, minWidth: 0, fontFamily: MONOSPACE_FONT }}
                          onBlur={handlePasswordBlur}
                        />
                      </Form.Item>
                      <Button onClick={handleGeneratePassword}>自動生成</Button>
                    </div>
                  </Form.Item>
                </Col>
              )}
              <Col span={12}>
                {isEdit ? (
                  <Form.Item
                    label="権限"
                    extra={hint('権限の変更は一覧の「権限付与」から行います')}
                  >
                    <StatusBadge tone="neutral">
                      {(user && userRole[user.role]) || '—'}
                    </StatusBadge>
                  </Form.Item>
                ) : (
                  <Form.Item
                    label="権限"
                    name="role"
                    extra={hint('登録後の変更は一覧の「権限付与」から行います')}
                  >
                    <Select options={CREATE_ROLE_OPTIONS} />
                  </Form.Item>
                )}
              </Col>
              <Col span={12}>
                <Form.Item
                  label="企業グループ"
                  extra={hint(
                    isEdit
                      ? 'グループの移動は CSV マージ機能を使用します'
                      : '一覧で選択中の企業に固定されます'
                  )}
                >
                  <Text>{groupLabel}</Text>
                </Form.Item>
              </Col>
            </Row>
          </div>

          {/* ② 基本情報 */}
          <div>
            {sectionTitle('② 基本情報')}
            <Row gutter={24}>
              <Col span={12}>
                <Form.Item
                  label="姓"
                  name="lastname"
                  rules={[{ required: true, message: '姓を入力してください' }]}
                >
                  <Input />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  label="名"
                  name="name"
                  rules={[{ required: true, message: '名を入力してください' }]}
                >
                  <Input />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label="姓（ヨミ）" name="lastname_yomi">
                  <Input />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label="名（ヨミ）" name="name_yomi">
                  <Input />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label="社員番号" name="emp_no">
                  <Input />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label="性別" name="sex">
                  <Select allowClear placeholder="選択してください" options={SEX_OPTIONS} />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label="生年月日" name="birthday">
                  <DatePicker style={{ width: '100%' }} format={DATE_FORMAT} />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label="入社日" name="join_company">
                  <DatePicker style={{ width: '100%' }} format={DATE_FORMAT} />
                </Form.Item>
              </Col>
            </Row>
          </div>

          {/* ③ 所属 */}
          <div>
            {sectionTitle('③ 所属')}
            <Row gutter={24}>
              <Col span={24}>
                {companyRule.enabled ? (
                  <Form.Item
                    label="会社名"
                    name="company"
                    // 系列会社ありグループでは権限を問わず必須（いずれかの系列会社に属する）
                    required
                    extra={hint('系列会社マスタから選択します。')}
                    rules={[
                      {
                        validator: (rule, value) => {
                          // 編集で会社名を変更しない場合は検証しない（サーバーと同じ。マスタ未登録の現在値でも他項目を保存できる）
                          if (isEdit && trimText(value) === trimText(user ? user.company : '')) {
                            return Promise.resolve();
                          }
                          const error = validateCompany(companyRule, value);
                          return error ? Promise.reject(new Error(error)) : Promise.resolve();
                        },
                      },
                    ]}
                  >
                    <Select
                      showSearch
                      allowClear
                      placeholder="系列会社を選択"
                      optionFilterProp="label"
                      options={companyOptions}
                      onChange={(value) => {
                        // 所属は会社名（系列会社）ごとの組織なので、会社名を変えたら所属を選び直す
                        if (!companyFilterActive) return;
                        setPickedCompany(trimText(value));
                        form.setFieldsValue({ deptPath: undefined });
                      }}
                    />
                  </Form.Item>
                ) : (
                  <Form.Item
                    label="会社名"
                    name="company"
                    extra={
                      isEdit ? undefined : hint('初期値は企業グループ名（編集可）')
                    }
                  >
                    <Input />
                  </Form.Item>
                )}
              </Col>
              <Col span={24}>
                {hasOrgTree ? (
                  <Form.Item
                    label="所属（所属①〜⑦）"
                    name="deptPath"
                    extra={hint(
                      cascaderNeedsCompany
                        ? '先に会社名を選択してください'
                        : '組織の追加・改名は「組織情報管理」画面で行います。保存時に組織表と照合し、存在しない所属は保存できません'
                    )}
                  >
                    <Cascader
                      disabled={cascaderNeedsCompany}
                      options={cascaderOptions}
                      changeOnSelect
                      showSearch
                      allowClear
                      placeholder="所属①／所属②… を選択"
                      style={{ width: '100%' }}
                    />
                  </Form.Item>
                ) : (
                  <Form.Item
                    label="所属（所属①〜⑦）"
                    name="deptText"
                    extra={hint(
                      '企業に登録済みの所属から選択します。候補に無い場合は「新しい所属を追加」で入力してください'
                    )}
                  >
                    <SelectWithAdd
                      itemLabel="所属"
                      addButtonLabel="新しい所属を追加"
                      options={deptSelectOptions}
                      addTitle="新しい所属を追加"
                      addFieldLabel="所属（フルパス）"
                      addPlaceholder="例）東京本社／営業部／営業１課"
                      addHint={`階層は「${PATH_SEP}」（全角スラッシュ）区切り・最大 ${MAX_DEPT_LEVEL} 階層（CSV の「所属フルパス」列と同じ書式）。途中の階層を空にすることはできません。ユーザー情報を保存すると企業の所属候補（マスター）にも登録されます。`}
                      validateValue={validateDeptPath}
                      normalizeValue={normalizeDeptPath}
                      onAddOption={(value) => addOption('department', value)}
                    />
                  </Form.Item>
                )}
              </Col>
              {isEdit && (
              <Col span={12}>
                <Form.Item
                  label="所属指定（E3）"
                  extra={hint('「閲覧範囲」画面で管理します')}
                >
                  {e3Departments.length > 0 ? (
                    e3Departments.map((dep, index) => (
                      <StatusBadge
                        key={index}
                        tone="neutral"
                        style={{ marginRight: 'var(--space-1)' }}
                      >
                        {dep}
                      </StatusBadge>
                    ))
                  ) : (
                    <Text type="secondary">—</Text>
                  )}
                </Form.Item>
              </Col>
              )}
              {isEdit && (
              <Col span={12}>
                <Form.Item
                  label="閲覧可能シーズン"
                  extra={hint('「閲覧範囲」画面で管理します')}
                >
                  <Text>
                    {((user && user.accessible_survey_seasons) || []).length} 件
                  </Text>
                </Form.Item>
              </Col>
              )}
            </Row>
          </div>

          {/* ④ 属性 */}
          <div>
            {sectionTitle('④ 属性')}
            <Row gutter={24}>
              {MASTER_FIELDS.map(({ key, label }) => (
                <Col span={12} key={key}>
                  <Form.Item label={label} name={key}>
                    <SelectWithAdd
                      itemLabel={label}
                      options={masterOptions[key]}
                      addTitle={`新しい${label}を追加`}
                      addFieldLabel={label}
                      addPlaceholder={`例）${label === '役職' ? '部長' : label}`}
                      addHint="追加した値は選択された状態になります。ユーザー情報を保存すると企業マスターにも登録されます（キャンセルすると登録されません）。既に候補にある値は追加できません。"
                      validateValue={validateMasterValue(key, label)}
                      onAddOption={(value) => addOption(key, value)}
                    />
                  </Form.Item>
                </Col>
              ))}
              <Col span={12}>
                <Form.Item label="勤務地" name="work_location">
                  <Input />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label="勤務時間制度" name="work_time_rule">
                  <Input />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label="勤務時間" name="work_time">
                  <Input />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label="入社区分" name="join_type">
                  <Input />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label="職種" name="job_type">
                  <Input />
                </Form.Item>
              </Col>
            </Row>
            <div style={{ ...HINT_STYLE, marginBottom: 'var(--space-4)' }}>
              役職・等級・雇用区分・出向は企業マスターの値から選択します（自由入力なし）。「追加」で候補を増やすと、保存時に企業マスターにも登録されます
            </div>
          </div>

          {/* ⑤ 状態（新規登録は集計対象外のみ。パスワード変更・初期パスワード等は編集のみ） */}
          <div>
            {sectionTitle('⑤ 状態')}
            <Row gutter={24}>
              <Col span={12}>
                <Form.Item
                  label="集計対象外"
                  name="inactive"
                  valuePropName="checked"
                  extra={hint('オンにすると組織集計の対象から除外されます')}
                >
                  <Switch />
                </Form.Item>
              </Col>
              {isEdit && (
                <Col span={12}>
                  <Form.Item label="パスワード変更">
                    {user && user.change_password ? (
                      <StatusBadge tone="success">本人変更済み</StatusBadge>
                    ) : (
                      <StatusBadge tone="neutral">
                        未変更（初期パスワードのまま）
                      </StatusBadge>
                    )}
                  </Form.Item>
                </Col>
              )}
              {isEdit && (
                <Col span={12}>
                  <Form.Item
                    label="初期パスワード"
                    extra={hint(
                      '登録時のスナップショット（本人が変更しても更新されません）'
                    )}
                  >
                    <Text style={{ fontFamily: 'Menlo, Consolas, monospace' }}>
                      {(user && user.init_pass) || '—'}
                    </Text>
                  </Form.Item>
                </Col>
              )}
              {isEdit && user && user.trial_campaign_key && (
                <Col span={12}>
                  <Form.Item label="トライアル（キャンペーンキー）">
                    <Text>{user.trial_campaign_key}</Text>
                  </Form.Item>
                </Col>
              )}
              {isEdit && user && user.trial_quick_login && (
                <Col span={12}>
                  <Form.Item label="トライアル簡易ログイン">
                    <StatusBadge tone="warning">
                      簡易ログイン中（メール未確認）
                    </StatusBadge>
                  </Form.Item>
                </Col>
              )}
            </Row>
          </div>

          {/* ⑥ 変更履歴（編集のみ） */}
          {isEdit && (
            <div>
              {sectionTitle(`⑥ 変更履歴（全 ${historyRows.length} 件）`)}
              <div style={NOTICE_STYLE}>
                所属・役職・等級・雇用区分・出向・集計対象外 を変更して保存すると、
                <b>最新の変更履歴 1 件が同じ内容に更新されます</b>
                （履歴は追加されません）。異動として履歴を残す場合は CSV
                更新（履歴付き）をご利用ください。履歴が 0
                件のユーザーは 1 件作成します。
              </div>
              <Table
                size="small"
                columns={historyColumns}
                dataSource={historyRows}
                rowKey="_rowKey"
                // 所属パスが長い場合はこの表だけを横スクロールさせる（モーダル本体は横スクロールさせない）
                scroll={{ x: 'max-content' }}
                pagination={{
                  pageSize: 10,
                  showSizeChanger: false,
                  showTotal: (total) => `全 ${total} 件`,
                }}
              />
            </div>
          )}

          {/* ⑦ 発行チケット（編集のみ・読み取り専用） */}
          {isEdit && (
            <div>
              {sectionTitle(`⑦ 発行チケット（全 ${ticketRows.length} 件）`)}
              <div style={{ marginBottom: 'var(--space-2)' }}>
                {hint(
                  'チケットの削除・回答差戻し・結果確認は一覧の「発行チケット確認」から行います'
                )}
              </div>
              <Table
                size="small"
                columns={ticketColumns}
                dataSource={ticketRows}
                rowKey="_rowKey"
                // サーベイ名が長い場合はこの表だけを横スクロールさせる（モーダル本体は横スクロールさせない）
                scroll={{ x: 'max-content' }}
                pagination={{
                  pageSize: 10,
                  showSizeChanger: false,
                  showTotal: (total) => `全 ${total} 件`,
                }}
              />
            </div>
          )}
        </>
        ) : null}
      </Form>
    </Modal>
  );
}

export default UserFormModal;
